import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { FieldHelp } from "@/components/ui/field-help";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { deskDocHelp } from "@/modules/core/help/desk-doc-help";
import { AppPicker } from "@/modules/shell/AppPicker";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  listPriceLists,
  listProducts,
  type PriceListRow,
  type ProductRow,
} from "@/shared/api/inventory";
import {
  createCustomer,
  createSalesOrder,
  listCustomers,
} from "@/shared/api/sales";
import { apiErrorCode, isApiError } from "@/shared/api/api-error";
import { createOverrideRequest } from "@/shared/api/overrides";
import { DocNumberField } from "@/modules/core/components/DocNumberField";
import { TaxAdvancedFields } from "@/modules/core/components/TaxAdvancedFields";
import { getOrgSettings } from "@/shared/api/org-settings";

type LineDraft = { productId: string; qty: string; unitPrice: string };
type CustomerRow = { id: string; name: string; priceListId: string | null };

export function SalesOrderNewPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [priceLists, setPriceLists] = useState<PriceListRow[]>([]);
  const [orgTaxPriceMode, setOrgTaxPriceMode] = useState("inclusive");
  const [orgTaxRatePct, setOrgTaxRatePct] = useState("12");
  const [taxPriceMode, setTaxPriceMode] = useState<
    "" | "inclusive" | "exclusive"
  >("");
  const [taxRatePct, setTaxRatePct] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [priceListId, setPriceListId] = useState("");
  const [newCustomerName, setNewCustomerName] = useState("");
  const [docNumber, setDocNumber] = useState("");
  const [customerRef, setCustomerRef] = useState("");
  const [paymentDueOn, setPaymentDueOn] = useState("");
  const [deliverBy, setDeliverBy] = useState("");
  const [fulfillmentMethod, setFulfillmentMethod] = useState<
    "" | "pickup" | "delivery"
  >("");
  const [discountPct, setDiscountPct] = useState("");
  const [overrideRequestId, setOverrideRequestId] = useState<string | null>(
    null,
  );
  const [lines, setLines] = useState<LineDraft[]>([
    { productId: "", qty: "1", unitPrice: "" },
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needDiscountOverride, setNeedDiscountOverride] = useState(false);

  useEffect(() => {
    if (!companySlug) return;
    void Promise.all([
      listCustomers(companySlug),
      listProducts(companySlug),
      listPriceLists(companySlug),
      getOrgSettings(companySlug),
    ]).then(([c, p, pl, o]) => {
      setCustomers(c.customers);
      setProducts(p.products);
      setPriceLists(pl.priceLists);
      setOrgTaxPriceMode(o.org.taxPriceMode);
      setOrgTaxRatePct(o.org.taxRatePct);
      if (c.customers[0]) {
        setCustomerId(c.customers[0].id);
        setPriceListId(c.customers[0].priceListId ?? "");
      }
    });
  }, [companySlug]);

  if (!companySlug) return null;

  async function ensureCustomer(): Promise<string> {
    if (customerId) return customerId;
    if (!newCustomerName.trim()) {
      throw new Error("Pick or create a customer");
    }
    const { customer } = await createCustomer(companySlug!, {
      name: newCustomerName.trim(),
    });
    setCustomers((prev) => [...prev, customer]);
    setCustomerId(customer.id);
    return customer.id;
  }

  async function onRequestDiscountOverride() {
    if (!companySlug) return;
    setBusy(true);
    setError(null);
    const base = {
      permissionKey: "pos.discount_over_cap",
      summary: `SO discount ${discountPct || "?"}% over soft cap`,
      payload: { discountPct },
      grantScope: "transaction" as const,
    };
    try {
      let request: { id: string; status: string };
      try {
        ({ request } = await createOverrideRequest(companySlug, {
          ...base,
          autoApprove: true,
        }));
      } catch {
        ({ request } = await createOverrideRequest(companySlug, base));
      }
      setOverrideRequestId(request.id);
      setError(
        request.status === "approved"
          ? "Override granted — submit again"
          : "Override requested — wait for approval, then submit",
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNeedDiscountOverride(false);
    try {
      const cid = await ensureCustomer();
      const clean = lines
        .filter((l) => l.productId && Number(l.qty) > 0)
        .map((l) => ({
          productId: l.productId,
          qty: l.qty,
          ...(l.unitPrice.trim() ? { unitPrice: l.unitPrice } : {}),
        }));
      if (clean.length === 0) throw new Error("Add at least one line");
      const base = {
        customerId: cid,
        lines: clean,
        ...(discountPct.trim() ? { discountPct: discountPct.trim() } : {}),
        ...(taxRatePct.trim() ? { taxRatePct: taxRatePct.trim() } : {}),
        ...(taxPriceMode ? { taxPriceMode } : {}),
        ...(docNumber.trim() ? { number: docNumber.trim() } : {}),
        ...(customerRef.trim() ? { customerRef: customerRef.trim() } : {}),
        ...(paymentDueOn ? { paymentDueOn } : {}),
        ...(deliverBy ? { deliverBy } : {}),
        ...(fulfillmentMethod
          ? { fulfillmentMethod }
          : {}),
        ...(priceListId ? { priceListId } : {}),
        ...(overrideRequestId
          ? { overrideRequestId }
          : {}),
        submit: true,
      };
      let order: { id: string; number: string };
      try {
        ({ order } = await createSalesOrder(companySlug!, base));
      } catch (err: unknown) {
        const code = apiErrorCode(err);
        if (code === "discount_over_cap" && isApiError(err)) {
          const ok = await confirmUi({
            title: "Discount over soft cap?",
            description: `${err.message}\n\nCreate this sales order with the high discount?`,
            confirmLabel: "Create anyway",
            destructive: true,
          });
          if (!ok) {
            setError("Cancelled");
            return;
          }
          ({ order } = await createSalesOrder(companySlug!, {
            ...base,
            confirmDiscountOverCap: true,
          }));
        } else if (code === "discount_over_cap_override" && isApiError(err)) {
          setError(err.message);
          setNeedDiscountOverride(true);
          return;
        } else {
          throw err;
        }
      }
      navigate(`/c/${companySlug}/sales/orders/${order.id}`, {
        replace: true,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
      <PageFrame>
      <PageHeader
        title="New sales order"
        description="Deliver and collect later — POS stays on the register"
      />
      <form className="mt-8 space-y-4" onSubmit={(e) => void onSubmit(e)}>
        <div>
          <Label htmlFor="so-customer">Customer</Label>
          <AppPicker
            id="so-customer"
            className="mt-1"
            title="Customer"
            description="Search customers"
            placeholder="Choose customer"
            clearLabel="— Create new below —"
            value={customerId || null}
            options={customers.map((s) => ({
              value: s.id,
              label: s.name,
              keywords: s.name,
            }))}
            onChange={(id) => {
              setCustomerId(id ?? "");
              const cust = customers.find((c) => c.id === id);
              setPriceListId(cust?.priceListId ?? "");
            }}
          />
        </div>
        {!customerId && (
          <div>
            <Label htmlFor="so-new-customer">New customer name</Label>
            <Input
              id="so-new-customer"
              className="mt-1"
              value={newCustomerName}
              onChange={(e) => setNewCustomerName(e.target.value)}
            />
          </div>
        )}
        <div>
          <Label htmlFor="so-discount">Order discount % (soft cap applies)</Label>
          <Input
            id="so-discount"
            className="mt-1 font-mono"
            value={discountPct}
            onChange={(e) => setDiscountPct(e.target.value)}
            placeholder="0"
            inputMode="decimal"
          />
        </div>
        {needDiscountOverride && (
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => void onRequestDiscountOverride()}
          >
            Request / self-allow override
          </Button>
        )}

        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
            Lines
          </div>
          {lines.map((line, i) => (
            <div key={i} className="grid grid-cols-3 gap-2">
              <AppPicker
                className="col-span-3"
                title="Product"
                description="Search products"
                placeholder="Product…"
                value={line.productId || null}
                options={products.map((p) => ({
                  value: p.id,
                  label: p.name,
                  keywords: `${p.name} ${p.sku ?? ""} ${p.barcode ?? ""}`,
                  description: p.sku ?? undefined,
                }))}
                onChange={(id) => {
                  const next = [...lines];
                  next[i] = { ...line, productId: id ?? "" };
                  setLines(next);
                }}
              />
              <Input
                className="font-mono"
                placeholder="Qty"
                value={line.qty}
                onChange={(e) => {
                  const next = [...lines];
                  next[i] = { ...line, qty: e.target.value };
                  setLines(next);
                }}
              />
              <Input
                className="col-span-2 font-mono text-right tabular-nums"
                inputMode="decimal"
                placeholder="Price (blank = list)"
                value={line.unitPrice}
                onChange={(e) => {
                  const next = [...lines];
                  next[i] = { ...line, unitPrice: e.target.value };
                  setLines(next);
                }}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              setLines((prev) => [
                ...prev,
                { productId: "", qty: "1", unitPrice: "" },
              ])
            }
          >
            Add line
          </Button>
        </div>

        <AdvancedSection subtitle="refs, dates & list">
          <DocNumberField
            companySlug={companySlug}
            docType="sales_order"
            value={docNumber}
            onChange={setDocNumber}
          />
          <div>
            <Label htmlFor="so-price-list">Price list</Label>
            <AppPicker
              id="so-price-list"
              className="mt-1"
              title="Price list"
              description="Search price lists"
              placeholder="Customer / org default"
              clearLabel="Customer / org default"
              value={priceListId || null}
              options={priceLists.map((l) => ({
                value: l.id,
                label: l.name,
                description: l.isDefaultPos ? "POS default" : undefined,
                keywords: `${l.name} ${l.code ?? ""}`,
              }))}
              onChange={(id) => setPriceListId(id ?? "")}
            />
          </div>
          <div>
            <Label htmlFor="so-customer-ref">Customer PO / ref</Label>
            <Input
              id="so-customer-ref"
              className="mt-1"
              value={customerRef}
              onChange={(e) => setCustomerRef(e.target.value)}
              maxLength={120}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="so-payment-due">Payment due</Label>
              <Input
                id="so-payment-due"
                type="date"
                className="mt-1"
                value={paymentDueOn}
                onChange={(e) => setPaymentDueOn(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="so-deliver-by">Deliver by</Label>
              <Input
                id="so-deliver-by"
                type="date"
                className="mt-1"
                value={deliverBy}
                onChange={(e) => setDeliverBy(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="so-fulfillment" className="inline-flex items-center">
              Fulfillment
              <FieldHelp content={deskDocHelp.fulfillmentMethod} />
            </Label>
            <Select
              id="so-fulfillment"
              className="mt-1"
              value={fulfillmentMethod}
              onChange={(e) =>
                setFulfillmentMethod(
                  e.target.value as "" | "pickup" | "delivery",
                )
              }
            >
              <option value="">Unset (direct deliver)</option>
              <option value="pickup">Pickup — customer collects</option>
              <option value="delivery">Delivery — ship / dispatch</option>
            </Select>
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              Pickup skips in-transit. Delivery can use In transit before
              deliver.
            </p>
          </div>
        </AdvancedSection>

        <TaxAdvancedFields
          orgTaxPriceMode={orgTaxPriceMode}
          orgTaxRatePct={orgTaxRatePct}
          taxPriceMode={taxPriceMode}
          taxRatePct={taxRatePct}
          onTaxPriceMode={setTaxPriceMode}
          onTaxRatePct={setTaxRatePct}
        />

        {error && <Alert variant="destructive">{error}</Alert>}
        <div className="flex gap-2 pt-2">
          <Button type="submit" pending={busy} pendingLabel="Saving…">
            Submit SO
          </Button>
          <Button variant="ghost" asChild>
            <Link
              to={`/c/${companySlug}/sales/orders`}
              className="no-underline"
            >
              Cancel
            </Link>
          </Button>
        </div>
      </form>
    </PageFrame>
  );
}
